import {tx, ptx, usePageText} from '../lib/page-text';
import { sessionEntryUrl } from '../lib/session-entry';
import { useAuth } from '../lib/auth-store';
import { RequestFeedback } from '../components/RequestFeedback';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { Modal } from '../components/ui';
import { PlusIcon } from '../components/icons';

interface Session {
  id: string;
  type: 'livestream' | 'call' | 'conference' | 'webinar';
  name: string;
  state: 'scheduled' | 'waiting' | 'live' | 'ended' | 'cancelled';
  projectId: string | null;
  hostId: string | null;
  recordingEnabled: boolean;
  layout: string;
  startedAt: string | null;
  endedAt: string | null;
  createdAt: string;
  _count?: { participants: number; recordings: number };
}

interface Project { id: string; name: string; slug: string }
interface Analytics {
  sessionsTotal: number;
  sessionsByType: Record<string, number>;
  sessionsByState: Record<string, number>;
  participantJoins30d: number;
  recordings30d: number;
}

export function SessionsPage() {
  usePageText();
  const editor=useAuth(state=>['OWNER','ADMIN','MEMBER'].includes(state.user?.role??''));
  const qc = useQueryClient();
  // ?create=1 — Templates' "Start a meeting" lands here with the form open.
  const [showCreate, setShowCreate] = useState(() => new URLSearchParams(window.location.search).get('create') === '1');
  const [selected, setSelected] = useState<string | null>(null);
  const [filter, setFilter] = useState<{ projectId?: string; type?: string; state?: string }>({});

  const sessions = useQuery<Session[]>({
    queryKey: ['sessions', filter],
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (filter.projectId) qs.set('projectId', filter.projectId);
      if (filter.type) qs.set('type', filter.type);
      if (filter.state) qs.set('state', filter.state);
      return (await api.get(`/me/sessions${qs.toString() ? '?' + qs : ''}`)).data;
    },
  });
  const analytics = useQuery<Analytics>({
    queryKey: ['session-analytics'],
    queryFn: async () => (await api.get('/me/sessions/analytics/summary')).data,
  });
  const projects = useQuery<Project[]>({
    queryKey: ['projects'],
    queryFn: async () => (await api.get('/me/projects')).data,
  });

  return (
    <div>
      <div className="row-between" style={{ marginBottom: 28 }}>
        <div>
          <h1 className="h1">{tx("Sessions")}</h1>
          <p className="subtitle" style={{ marginBottom: 0 }}>{tx("Every livestream, conference, webinar and 1:1 call across your projects.")}</p>
        </div>
        <button className="btn lg" disabled={!editor} onClick={() => setShowCreate(true)}>{ptx("{v0} New session", {v0: (<PlusIcon size={16}/>)})}</button>
      </div>

      {/* KPI strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px,1fr))', gap: 12, marginBottom: 24 }}>
        <Kpi label={tx("Total")} value={analytics.data?.sessionsTotal ?? '—'} />
        <Kpi label={tx("Live now")} value={analytics.data ? (analytics.data?.sessionsByState?.live ?? 0) : '—'} />
        <Kpi label={tx("Waiting")} value={analytics.data ? (analytics.data?.sessionsByState?.waiting ?? 0) : '—'} />
        <Kpi label={tx("Joins · 30d")} value={analytics.data?.participantJoins30d ?? '—'} />
        <Kpi label={tx("Recordings · 30d")} value={analytics.data?.recordings30d ?? '—'} />
        <Kpi label={tx("Calls")} value={analytics.data ? (analytics.data?.sessionsByType?.call ?? 0) : '—'} />
        <Kpi label={tx("Conferences")} value={analytics.data ? (analytics.data?.sessionsByType?.conference ?? 0) : '—'} />
        <Kpi label={tx("Livestreams")} value={analytics.data ? (analytics.data?.sessionsByType?.livestream ?? 0) : '—'} />
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14, alignItems: 'center' }}>
        <span className="muted" style={{ fontSize: 12, fontWeight: 600 }}>{tx("FILTER")}</span>
        <select className="input" style={{ width: 180 }} value={filter.projectId ?? ''} onChange={(e) => setFilter((f) => ({ ...f, projectId: e.target.value || undefined }))}>
          <option value="">{tx("All projects")}</option>
          {projects.data?.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select className="input" style={{ width: 150 }} value={filter.type ?? ''} onChange={(e) => setFilter((f) => ({ ...f, type: e.target.value || undefined }))}>
          <option value="">{tx("All types")}</option>
          <option value="livestream">{tx("Livestream")}</option>
          <option value="call">{tx("Call")}</option>
          <option value="conference">{tx("Conference")}</option>
          <option value="webinar">{tx("Webinar")}</option>
        </select>
        <select className="input" style={{ width: 130 }} value={filter.state ?? ''} onChange={(e) => setFilter((f) => ({ ...f, state: e.target.value || undefined }))}>
          <option value="">{tx("All states")}</option>
          <option value="waiting">{tx("Waiting")}</option>
          <option value="live">{tx("Live")}</option>
          <option value="ended">{tx("Ended")}</option>
          <option value="cancelled">{tx("Cancelled")}</option>
        </select>
      </div>

      <RequestFeedback pending={sessions.isPending} failed={sessions.isError} hasData={!!sessions.data} retry={() => void sessions.refetch()} retrying={sessions.isFetching} />
      <div className={`dashboard-inspector-grid${selected ? ' has-selection' : ''}`} hidden={!sessions.data}>
        <div className="card dashboard-table" style={{ padding: 0 }}>
          {sessions.data && sessions.data.length === 0
            ? <div style={{ padding: 32, textAlign: 'center' }} className="muted">{tx("No sessions yet. Choose New session to create a meeting room.")}</div>
            : (
              <table>
                <thead><tr><th>{tx("Session")}</th><th>{tx("Type")}</th><th>{tx("State")}</th><th>{tx("Participants")}</th><th>{tx("Recordings")}</th><th>{tx("Started")}</th></tr></thead>
                <tbody>
                  {sessions.data?.map((s) => (
                    <tr key={s.id} style={{ cursor: 'pointer', background: selected === s.id ? 'rgba(0,210,255,0.08)' : undefined }}
                        onClick={() => setSelected(s.id)}>
                      <td>
                        <button className="dashboard-row-action" onClick={() => setSelected(s.id)}>{s.name}</button>
                        <div className="muted mono" style={{ fontSize: 11 }}>{s.id}</div>
                      </td>
                      <td><span className="badge">{s.type}</span></td>
                      <td><StateBadge state={s.state}/></td>
                      <td className="muted">{s._count?.participants ?? 0}</td>
                      <td className="muted">{s._count?.recordings ?? 0}</td>
                      <td className="muted">{s.startedAt ? relativeTime(s.startedAt) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </div>

        {selected && (
          <SessionInspector key={selected}
            sessionId={selected}
            onClose={() => setSelected(null)}
            onChange={() => {
              qc.invalidateQueries({ queryKey: ['sessions'] });
              qc.invalidateQueries({ queryKey: ['session-analytics'] });
            }}
          />
        )}
      </div>

      {showCreate && editor && <RequestFeedback pending={projects.isPending} failed={projects.isError} hasData={!!projects.data} retry={()=>void projects.refetch()}/>}
      {showCreate && editor && projects.data && (
        <CreateSessionModal
          projects={projects.data}
          onClose={() => setShowCreate(false)}
          onCreated={(created) => { setShowCreate(false); setSelected(created.id); qc.invalidateQueries({ queryKey: ['sessions'] }); qc.invalidateQueries({ queryKey: ['session-analytics'] }); }}
        />
      )}
    </div>
  );
}

function StateBadge({ state }: { state: string }) {
  usePageText();
  if (state === 'live') return <span className="badge live">{ptx("{v0}LIVE", {v0: (<span className="pulse"/>)})}</span>;
  if (state === 'waiting') return <span className="badge warn">{tx("waiting")}</span>;
  if (state === 'ended') return <span className="badge">{tx("ended")}</span>;
  if (state === 'cancelled') return <span className="badge warn">{tx("cancelled")}</span>;
  return <span className="badge">{state}</span>;
}

function Kpi({ label, value }: { label: string; value: number | string }) {
  usePageText();
  return (
    <div className="card" style={{ padding: '12px 14px' }}>
      <div className="muted" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 600, marginTop: 4 }}>{value}</div>
    </div>
  );
}

function relativeTime(iso: string): string {
  const d = Date.now() - new Date(iso).getTime();
  if (d < 60_000) return 'just now';
  if (d < 3_600_000) return `${Math.floor(d / 60_000)}m ago`;
  if (d < 86_400_000) return `${Math.floor(d / 3_600_000)}h ago`;
  return new Date(iso).toLocaleDateString();
}

// ─── inspector ──────────────────────────────────────────────────
interface SessionDetail extends Session {
  locked: boolean;
  waitingRoom: boolean;
  participants: Array<{
    id: string; userId: string; displayName: string | null; role: string; state: string;
    canPublishAudio: boolean; canPublishVideo: boolean; canPublishScreen: boolean;
    handRaisedAt: string | null; spotlit: boolean; joinedAt: string | null;
  }>;
  recordings: Array<{ id: string; layout: string; state: string; startedAt: string; endedAt: string | null }>;
}

function SessionInspector({ sessionId, onClose, onChange }: { sessionId: string; onClose: () => void; onChange: () => void }) {
  usePageText();
  const actor=useAuth(state=>state.user),editor=['OWNER','ADMIN','MEMBER'].includes(actor?.role??'');
  const [hostEntry,setHostEntry]=useState<{url:string;expiresAt:string}|null>(null),[guestEntry,setGuestEntry]=useState<{url:string;expiresAt:string}|null>(null),[guestName,setGuestName]=useState('');
  const [copyStatus,setCopyStatus]=useState('');
  const qc = useQueryClient();
  const prepareHost=useMutation({mutationFn:async()=>{
    const data=(await api.post(`/me/sessions/${sessionId}/token`,{userId:'workspace-'+actor!.id,displayName:actor?.name||'Host',role:'host'})).data;
    return {url:sessionEntryUrl(sessionId,data.token,actor?.name||'Host',true),expiresAt:data.expiresAt};
  },onSuccess:data=>{setHostEntry(data);void qc.invalidateQueries({queryKey:['session',sessionId]});}});
  const inviteGuest=useMutation({mutationFn:async()=>{
    const name=guestName.trim()||'Invited guest';const data=(await api.post(`/me/sessions/${sessionId}/token`,{userId:'invite-'+crypto.randomUUID(),displayName:name,role:'guest'})).data;
    return {url:sessionEntryUrl(sessionId,data.token,name),expiresAt:data.expiresAt};
  },onSuccess:data=>{setGuestEntry(data);setCopyStatus('');void qc.invalidateQueries({queryKey:['session',sessionId]});}});
  const session = useQuery<SessionDetail>({
    queryKey: ['session', sessionId],
    refetchInterval: 5000,
    queryFn: async () => (await api.get(`/me/sessions/${sessionId}`)).data,
  });

  const endSession = useMutation({
    mutationFn: async () => (await api.post(`/me/sessions/${sessionId}/end`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['session', sessionId] }); onChange(); },
  });
  const muteAll = useMutation({
    mutationFn: async () => (await api.post(`/me/sessions/${sessionId}/moderate/mute-all`, { exceptHost: true })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['session', sessionId] }),
  });
  const lock = useMutation({
    mutationFn: async (locked: boolean) => (await api.patch(`/me/sessions/${sessionId}`, { locked })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['session', sessionId] }),
  });
  const setLayout = useMutation({
    mutationFn: async (layout: string) => (await api.patch(`/me/sessions/${sessionId}`, { layout })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['session', sessionId] }),
  });
  const spotlight = useMutation({
    mutationFn: async (userId: string | null) => (await api.post(`/me/sessions/${sessionId}/moderate/spotlight`, { userId })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['session', sessionId] }),
  });
  const promote = useMutation({
    mutationFn: async (userId: string) => (await api.post(`/me/sessions/${sessionId}/moderate/promote`, { userId, role: 'cohost' })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['session', sessionId] }),
  });
  const kick = useMutation({
    mutationFn: async (userId: string) => (await api.delete(`/me/sessions/${sessionId}/participants/${encodeURIComponent(userId)}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['session', sessionId] }),
  });

  const admit = useMutation({
    mutationFn: async (userId: string) => (await api.post(`/me/sessions/${sessionId}/participants/${encodeURIComponent(userId)}/admit`)).data,
    onSuccess: () => qc.invalidateQueries({queryKey:['session',sessionId]}),
  });
  const actions = [prepareHost,inviteGuest,endSession,muteAll,lock,setLayout,spotlight,promote,kick,admit];
  const busy = actions.some(action=>action.isPending);
  const error = actions.find(action=>action.isError)?.error as any;
  if (!session.data) return <div className="card dashboard-inspector"><RequestFeedback pending={session.isPending} failed={session.isError} retry={()=>void session.refetch()}/></div>;
  const s = session.data;

  return (
    <div className="card dashboard-inspector" style={{ padding: 16, position: 'sticky', top: 16, alignSelf: 'flex-start', maxHeight: 'calc(100vh - 100px)', overflow: 'auto' }}>
      <div className="row-between" style={{ marginBottom: 8 }}>
        <div style={{ fontWeight: 600 }}>{s.name}</div>
        <button className="btn outline sm" onClick={onClose}>{tx("Close")}</button>
      </div>
      <div style={{ fontSize: 12, marginBottom: 14 }}>
        <div className="muted">{tx("Type: ")}<span style={{ color: 'var(--text)' }}>{s.type}</span>{tx(" · State: ")}<StateBadge state={s.state}/></div>
        <div className="muted">{tx("ID: ")}<span className="mono">{sessionId}</span></div>
      </div>

      {['live','waiting'].includes(s.state) && editor && <section aria-label={tx("Join and invite")} className="card" style={{marginBottom:16,padding:12}}>
        <h3 style={{marginTop:0}}>{tx("Join and invite")}</h3>
        <p className="muted" style={{fontSize:12}}>{tx("Enter with devices off, then turn them on when ready. Your host link is private.")}</p>
        <button className="btn" disabled={busy||!editor} onClick={()=>prepareHost.mutate()}>{prepareHost.isPending?tx("Preparing…"):tx("Prepare host entry")}</button>
        {hostEntry&&<p><a className="btn outline" href={hostEntry.url} target="_blank" rel="noopener noreferrer">{tx("Open meeting ↗")}</a><span className="muted" style={{display:'block',fontSize:12}}>{ptx("Enter before {v0}.", {v0: (new Date(hostEntry.expiresAt).toLocaleTimeString())})}</span></p>}
        <label className="label" htmlFor="session-guest-name" style={{marginTop:14}}>{tx("Guest name (optional)")}</label>
        <input id="session-guest-name" className="input" maxLength={120} value={guestName} onChange={e=>setGuestName(e.target.value)} placeholder={tx("Name for this invitation")}/>
        <button className="btn outline" style={{marginTop:8}} disabled={busy||s.locked} onClick={()=>inviteGuest.mutate()}>{inviteGuest.isPending?tx("Creating…"):tx("Create guest link")}</button>
        {s.locked&&<p className="muted">{tx("Unlock the room before inviting a guest.")}</p>}
        {guestEntry&&<div style={{marginTop:12}}><label className="label" htmlFor="session-guest-link">{tx("Guest invitation link")}</label><input id="session-guest-link" className="input mono" readOnly value={guestEntry.url} onFocus={e=>e.currentTarget.select()}/><button className="btn outline sm" style={{marginTop:8}} onClick={()=>navigator.clipboard.writeText(guestEntry.url).then(()=>setCopyStatus('Guest link copied'),()=>setCopyStatus('Select the link and copy it manually.'))}>{tx("Copy guest link")}</button>{copyStatus&&<p role="status" className="muted">{copyStatus}</p>}<p className="muted" style={{fontSize:12}}>{ptx("Share only with the intended guest; anyone holding this link can use it. Enter before {v0}. No email is sent. Use Remove beside the participant to revoke access.", {v0: (new Date(guestEntry.expiresAt).toLocaleTimeString())})}</p></div>}
      </section>}
      {['live','waiting'].includes(s.state) && editor && (
        <>
          <h4 style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-mute)', marginBottom: 6 }}>{tx("Moderation")}</h4>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
            <button disabled={busy||!editor} className="btn outline sm" onClick={() => muteAll.mutate()}>{tx("Mute all")}</button>
            <button disabled={busy||!editor} className="btn outline sm" onClick={() => lock.mutate(!s.locked)}>{ptx("{v0} room", {v0: (s.locked ? tx("Unlock") : tx("Lock"))})}</button>
            <span className="muted" style={{fontSize:12}}>{tx("Meeting recording is not connected. Use a Live Stream for automatic replays.")}</span>
            <button disabled={busy||!editor} className="btn outline sm" onClick={() => { if (confirm('End session for everyone?')) endSession.mutate(); }}>{tx("End session")}</button>
          </div>

          <h4 style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-mute)', marginBottom: 6 }}>{tx("Layout")}</h4>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
            {(['grid', 'speaker', 'sidebar', 'spotlight'] as const).map((l) => (
              <button key={l} className={`btn sm ${s.layout === l ? '' : 'outline'}`} onClick={() => setLayout.mutate(l)}>{l}</button>
            ))}
          </div>
        </>
      )}

      <p className="muted" style={{fontSize:12}}>{tx("End, remove and microphone permissions reach connected media within about 8 seconds. A locked room refuses new guest connections, including reconnects. Existing calls continue.")}</p>
      {busy && <p role="status">{tx("Saving moderation change…")}</p>}
      {error && <p role="alert" className="error">{error?.response?.data?.message ?? tx("Could not save this change. Try again.")}</p>}
      <h4 style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-mute)', marginBottom: 6 }}>{ptx("Participants ({v0})", {v0: (s.participants.length)})}</h4>
      <div style={{ marginBottom: 14, maxHeight: 320, overflow: 'auto' }}>
        {s.participants.map((p) => (
          <div key={p.id} style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--border, #1a1f2a)' }}>
            <div style={{ fontSize: 12, flex: '1 1 150px' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                <strong>{p.displayName ?? p.userId}</strong>
                <span className="badge" style={{ fontSize: 10 }}>{p.role}</span>
                {p.spotlit && <span className="badge live" style={{ fontSize: 10 }}>{tx("★ spotlight")}</span>}
                {p.handRaisedAt && <span className="badge warn" style={{ fontSize: 10 }}>{tx("✋ hand")}</span>}
              </div>
              <div className="muted" style={{ fontSize: 10 }}>
                {p.state}{p.canPublishAudio ? '' : ' · 🔇'}{p.canPublishVideo ? '' : tx(" · cam off")}
              </div>
            </div>
            {editor && ['live','waiting'].includes(s.state) && !['left','kicked'].includes(p.state) && (
              <div style={{ display: 'flex', flexWrap:'wrap', gap: 4 }}>
                <button className="btn outline sm" style={{ padding: '4px 8px', fontSize: 12, whiteSpace:'nowrap' }} onClick={() => spotlight.mutate(p.spotlit ? null : p.userId)}>{p.spotlit ? tx("Unspotlight") : tx("Spotlight")}</button>
                {p.state === 'waiting' && <button className="btn outline sm" disabled={busy||!editor} onClick={()=>admit.mutate(p.userId)}>{tx("Admit")}</button>}
                {p.role === 'guest' && <button className="btn outline sm" style={{ padding: '4px 8px', fontSize: 12, whiteSpace:'nowrap' }} onClick={() => promote.mutate(p.userId)}>{tx("Make cohost")}</button>}
                <button className="btn outline sm" style={{ padding: '4px 8px', fontSize: 12, whiteSpace:'nowrap' }} onClick={() => { if (confirm(`Kick ${p.userId}?`)) kick.mutate(p.userId); }}>{tx("Remove")}</button>
              </div>
            )}
          </div>
        ))}
      </div>

      {s.recordings.length > 0 && (
        <>
          <h4 style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-mute)', marginBottom: 6 }}>{tx("Recordings")}</h4>
          <div>
            {s.recordings.map((r) => (
              <div key={r.id} style={{ fontSize: 11, padding: '4px 0', display: 'flex', justifyContent: 'space-between' }}>
                <span>{r.layout} · <span className="badge">{r.state}</span></span>
                <span className="muted">{new Date(r.startedAt).toLocaleTimeString()}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─── create modal ──────────────────────────────────────────────
function CreateSessionModal({ projects, onClose, onCreated }: { projects: Project[]; onClose: () => void; onCreated: (session:Session) => void }) {
  usePageText();
  const [type, setType] = useState<'conference' | 'livestream' | 'webinar' | 'call'>('conference');
  const [name, setName] = useState('');
  const [projectId, setProjectId] = useState(projects[0]?.id ?? '');
  const recording = false;
  const [waitingRoom, setWaitingRoom] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: async () => (await api.post('/me/sessions', {
      type, name: name || `${type} ${new Date().toLocaleString()}`,
      projectId: projectId || undefined,
      recordingEnabled: recording,
      transcriptionEnabled: false,
      waitingRoom,
      layout: type === 'livestream' ? 'spotlight' : type === 'call' ? 'speaker' : 'grid',
    })).data,
    onSuccess: onCreated,
    onError: (e: any) => setErr(e?.response?.data?.message ?? 'Could not create'),
  });

  return (
    <Modal onClose={onClose}>
      <h2>{tx("New session")}</h2>
      <p className="modal-subtitle">{tx("Create a meeting room, invite participants and manage it here.")}</p>

      <label className="label">{tx("Type")}</label>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {(['conference', 'livestream', 'webinar', 'call'] as const).map((t) => (
          <button key={t} type="button" className={`btn sm ${type === t ? '' : 'outline'}`} onClick={() => setType(t)}>{t}</button>
        ))}
      </div>

      <label className="label" htmlFor="session-name">{tx("Name")}</label>
      <input id="session-name" className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder={`e.g. ${type === 'call' ? 'sales call with Acme' : 'Q4 all-hands'}`}/>

      <div style={{ height: 12 }}/>
      <label className="label" htmlFor="session-project">{tx("Project")}</label>
      <select id="session-project" className="input" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
        <option value="">{tx("No project")}</option>
        {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>

      <div style={{ height: 14 }}/>
      <div style={{ display: 'flex', gap: 12, flexWrap:'wrap',fontSize: 13 }}>
        <span className="muted">{tx("Meeting recording is not connected yet.")}</span>
        <span className="muted">{tx("Meeting captions are not connected yet.")}</span>
        <label>{ptx("{v0} Waiting room", {v0: (<input type="checkbox" checked={waitingRoom} onChange={(e) => setWaitingRoom(e.target.checked)}/>)})}</label>
      </div>

      {err && <div className="error" style={{ marginTop: 12 }}>{err}</div>}
      <div className="row-between" style={{ marginTop: 18 }}>
        <button className="btn outline" onClick={onClose}>{tx("Cancel")}</button>
        <button className="btn" disabled={create.isPending} onClick={() => create.mutate()}>
          {create.isPending ? tx("Creating…") : tx("Create session")}
        </button>
      </div>
    </Modal>
  );
}
